import React, { createContext, useContext, useEffect, useState } from 'react'
import { id } from '@instantdb/react'
import { db } from '../lib/db'

export interface AuthUser {
  id: string
  email?: string | null
  user_metadata?: {
    full_name?: string
    [key: string]: any
  }
}

interface AuthContextType {
  user: AuthUser | null
  session: { user: AuthUser } | null
  loading: boolean
  /** Step 1: send Instant magic code (also used for new signups) */
  sendMagicCode: (email: string) => Promise<{ error: Error | null }>
  /** Step 2: verify magic code; optionally set full name on first signup */
  verifyMagicCode: (
    email: string,
    code: string,
    fullName?: string
  ) => Promise<{ error: Error | null; user: AuthUser | null }>
  signUp: (
    email: string,
    password: string,
    fullName: string
  ) => Promise<{ error: Error | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: Error | null }>
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>
  updateUser: (updates: any) => Promise<{ error: Error | null }>
  refreshSession: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

function toAuthUser(instantUser: any, fullName?: string): AuthUser {
  return {
    id: instantUser.id,
    email: instantUser.email,
    user_metadata: {
      full_name:
        fullName ||
        instantUser.fullName ||
        instantUser.user_metadata?.full_name ||
        '',
    },
  }
}

/**
 * Creates/updates the signed-in user's own userPreferences + profiles rows.
 * Uses the ordinary client SDK (never the admin SDK) — Instant's permission
 * rules already allow a signed-in user to create and update their own rows
 * here, so no elevated privilege is needed for this part.
 */
async function ensureUserProfile(user: AuthUser, token: string | undefined, fullName?: string) {
  const name =
    fullName ||
    user.user_metadata?.full_name ||
    user.email?.split('@')[0] ||
    'Client'
  const now = Date.now()

  try {
    const { data } = await db.queryOnce({
      userPreferences: { $: { where: { userId: user.id } } },
      profiles: { $: { where: { userId: user.id } } },
    })

    const txs: any[] = []

    if (!data.userPreferences?.length) {
      const prefId = id()
      txs.push(
        db.tx.userPreferences[prefId]
          .update({
            userId: user.id,
            email: user.email || '',
            fullName: name,
            fitnessGoals: [],
            createdAt: now,
            updatedAt: now,
            lastLogin: now,
          })
          .link({ user: user.id })
      )
    } else {
      txs.push(
        db.tx.userPreferences[data.userPreferences[0].id].update({
          lastLogin: now,
          updatedAt: now,
        })
      )
    }

    if (!data.profiles?.length) {
      const profileId = id()
      txs.push(
        db.tx.profiles[profileId]
          .update({
            userId: user.id,
            fullName: name,
            isOnline: true,
            lastSeen: now,
            createdAt: now,
            updatedAt: now,
          })
          .link({ user: user.id })
      )
    }

    if (txs.length) {
      await db.transact(txs)
    }

    // Consume any pending Stripe enrollment(s) placed before this person had
    // an account yet (paid via a package enrollment link, then signed up).
    // This genuinely needs elevated privilege (pendingEnrollments/userPackages
    // are admin-only by permission rule), so it's a server call, authorized
    // by the caller's own verified session token — never a client-supplied id.
    if (user.email && token) {
      await consumePendingEnrollments(token)
    }
  } catch (err) {
    console.error('Failed to ensure user profile:', err)
  }
}

async function consumePendingEnrollments(token: string) {
  try {
    const res = await fetch('/api/auth/consume-enrollment', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      console.error('Failed to consume pending enrollment(s):', body?.error || res.status)
    }
  } catch (err) {
    console.error('Failed to consume pending enrollment(s):', err)
  }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const auth = db.useAuth()
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (auth.isLoading) {
      setLoading(true)
      return
    }
    if (auth.user) {
      let cancelled = false
      const next = toAuthUser(auth.user)
      setUser(next)
      setLoading(false)

      ;(async () => {
        await ensureUserProfile(next, auth.user?.refresh_token)
        try {
          const { data } = await db.queryOnce({
            userPreferences: { $: { where: { userId: next.id } } },
          })
          const storedName = data.userPreferences?.[0]?.fullName?.trim()
          if (!cancelled && storedName) {
            setUser((prev) =>
              prev
                ? {
                    ...prev,
                    user_metadata: {
                      ...prev.user_metadata,
                      full_name: storedName,
                    },
                  }
                : prev
            )
          }
        } catch (err) {
          console.error('Failed to load display name:', err)
        }
      })()

      return () => {
        cancelled = true
      }
    } else {
      setUser(null)
      setLoading(false)
    }
  }, [auth.isLoading, auth.user])

  const sendMagicCode = async (email: string) => {
    try {
      await db.auth.sendMagicCode({ email: email.trim().toLowerCase() })
      return { error: null }
    } catch (err: any) {
      console.error('sendMagicCode failed:', err)
      return {
        error: new Error(
          err?.body?.message ||
            err?.message ||
            'Failed to send code. Please try again in a moment.'
        ),
      }
    }
  }

  const verifyMagicCode = async (
    email: string,
    code: string,
    fullName?: string
  ) => {
    try {
      const result = await db.auth.signInWithMagicCode({
        email: email.trim().toLowerCase(),
        code: code.trim(),
        ...(fullName
          ? { extraFields: { fullName: fullName.trim() } }
          : {}),
      })
      const next = toAuthUser(result.user, fullName)
      setUser(next)
      await ensureUserProfile(next, (result.user as any)?.refresh_token, fullName)
      return { error: null, user: next }
    } catch (err: any) {
      return {
        error: new Error(err?.body?.message || err?.message || 'Invalid code'),
        user: null,
      }
    }
  }

  const signUp = async (email: string, _password: string, fullName: string) => {
    // Instant creates the account on first successful magic-code verify
    const { error } = await sendMagicCode(email)
    if (error) return { error }
    // Stash name for the verify step (Login/SignUp pages pass it to verifyMagicCode)
    try {
      sessionStorage.setItem('pending_signup_name', fullName)
      sessionStorage.setItem('pending_signup_email', email.trim().toLowerCase())
    } catch {
      /* ignore */
    }
    return { error: null }
  }

  const signOut = async () => {
    try {
      if (user?.id) {
        const { data } = await db.queryOnce({
          profiles: { $: { where: { userId: user.id } } },
        })
        if (data.profiles?.[0]) {
          await db.transact(
            db.tx.profiles[data.profiles[0].id].update({
              isOnline: false,
              lastSeen: Date.now(),
            })
          )
        }
      }
    } catch {
      /* ignore */
    }
    await db.auth.signOut()
    setUser(null)
    try {
      localStorage.removeItem('fitbycassie_session')
      localStorage.removeItem('adminStatus')
    } catch {
      /* ignore */
    }
  }

  const resetPassword = async (_email: string) => ({
    error: new Error(
      'Password reset is not used with InstantDB. Sign in with an email magic code instead.'
    ),
  })

  const updatePassword = async (_newPassword: string) => ({
    error: new Error('Passwords are not used with InstantDB magic-code auth.'),
  })

  const updateUser = async (updates: any) => {
    if (!user) return { error: new Error('Not signed in') }
    try {
      const fullName = updates?.data?.full_name || updates?.full_name
      if (fullName) {
        const { data } = await db.queryOnce({
          userPreferences: { $: { where: { userId: user.id } } },
          profiles: { $: { where: { userId: user.id } } },
        })
        const txs: any[] = []
        if (data.userPreferences?.[0]) {
          txs.push(
            db.tx.userPreferences[data.userPreferences[0].id].update({
              fullName,
              updatedAt: Date.now(),
            })
          )
        }
        if (data.profiles?.[0]) {
          txs.push(
            db.tx.profiles[data.profiles[0].id].update({
              fullName,
              updatedAt: Date.now(),
            })
          )
        }
        if (txs.length) await db.transact(txs)
        setUser((prev) =>
          prev
            ? {
                ...prev,
                user_metadata: { ...prev.user_metadata, full_name: fullName },
              }
            : prev
        )
      }
      return { error: null }
    } catch (err: any) {
      return { error: new Error(err?.message || 'Update failed') }
    }
  }

  const refreshSession = async () => {
    // Instant manages refresh tokens internally
  }

  const session = user ? { user } : null

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        sendMagicCode,
        verifyMagicCode,
        signUp,
        signOut,
        resetPassword,
        updatePassword,
        updateUser,
        refreshSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
